import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  CheckCircle2,
  Clock,
  Flame,
  Lock,
  Repeat,
  Scale,
  Share2,
  ShieldCheck,
  TrendingDown,
  Users,
} from "lucide-react";
import api from "../lib/api";
import LoanSimulator from "../components/LoanSimulator";
import ReserveMySpot from "../components/ReserveMySpot";

/**
 * TheCase
 *
 * Persuasion funnel aimed at low-credit car buyers (and anyone else being
 * extracted by predatory finance). The whole purpose of this page is a
 * single conversion: sign the Proposed Trillion Dollar Spending Power
 * Federal Credit Union pledge card + fill the 10-question NCUA survey.
 *
 * The pledge/survey form already exists on /credit-union#subscribe — this
 * page funnels there with a single, powerful CTA so there is ONE source of
 * truth for the submission.
 *
 * NCUA compliance: we never promise a loan, a rate, a product, or
 * membership. Every forward-looking claim is suffixed "on charter approval"
 * or "proposed". The page is educational and solicits charter support only.
 */

const PAIN_STATS = [
  {
    big: "2.0×",
    label: "Higher APR",
    body:
      "Black car buyers are quoted up to 2× the APR of white buyers with the exact same credit profile. (CFPB · DOJ consent order data)",
  },
  {
    big: "76%",
    label: "Subprime Traps",
    body:
      "Of all auto loans sold to Black buyers, 76% are originated through dealer-markup programs — the single most expensive channel.",
  },
  {
    big: "$3,200",
    label: "Avg. extra per loan",
    body:
      "The average Black household overpays $3,200 per auto loan vs. a credit-union-eligible borrower. Over three cars, that's a down payment on a home.",
  },
  {
    big: "35%",
    label: "Repossession rate",
    body:
      "Subprime auto loans sold to Black buyers repossess at 35% — three times the national average. The lender takes the car AND the payments already made.",
  },
];

const CYCLE_NODES = [
  {
    icon: Clock,
    title: "You walk onto the lot.",
    body:
      "The dealer runs your credit, finds a subprime tier, and quotes a price built around maximum profit — not your budget.",
  },
  {
    icon: TrendingDown,
    title: "The APR is marked up.",
    body:
      "Behind the scenes the lender approved you at 14%. The dealer sells it to you at 22% and keeps the 8-point spread.",
  },
  {
    icon: Flame,
    title: "Payments burn.",
    body:
      "You pay 60–72 months. Over half of every dollar is interest. The car depreciates faster than you build equity.",
  },
  {
    icon: Repeat,
    title: "Repossession or roll-over.",
    body:
      "One missed check and the car is gone. Or you trade it in — still underwater — and the negative equity rolls into the next loan. The cycle restarts.",
  },
];

const CHANGES = [
  {
    icon: Scale,
    title: "Member-owned pricing",
    body:
      "On charter approval, loans at TDSP FCU would be priced by a volunteer credit committee — not a profit motive. Dividends return to members, not shareholders.",
  },
  {
    icon: ShieldCheck,
    title: "No dealer markup",
    body:
      "Pre-approved financing in your pocket before you step on the lot. The dealer sells the car. We finance it. No spread.",
  },
  {
    icon: Users,
    title: "Credit-builder path",
    body:
      "Payment history reported to all three bureaus. Second-chance underwriting that looks at your rent, utility, and employment track record — not just a FICO snapshot.",
  },
  {
    icon: Banknote,
    title: "Every dollar circulates HOME",
    body:
      "Interest you pay stops funding Wall Street. It funds the next member's mortgage, small business loan, and emergency fund — all inside the proposed field of membership.",
  },
];

const ASKS = [
  {
    label: "Sign the pledge membership card",
    detail: "90 seconds. Non-binding. Held in escrow at Citizens Trust Bank. Refundable in full if the NCUA does not grant the charter.",
  },
  {
    label: "Answer the 10-question NCUA survey",
    detail: "6 minutes. Required by the NCUA to prove community need. No sensitive data — no SSN, no FICO, no account numbers.",
  },
  {
    label: "Share this page with 3 people in your zip code",
    detail: "Each new pledge inside Fulton · DeKalb · College Park moves us toward the 3,000 signatures the NCUA needs to see.",
  },
];

/* ─────────── small helpers ─────────── */

function AnimateIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(() => setShown(true), delay);
            io.disconnect();
          }
        });
      },
      { threshold: 0.18 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [delay]);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </div>
  );
}

function AnimatedCounter({ to, suffix = "", duration = 1800 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const start = performance.now();
            const tick = (t) => {
              const p = Math.min(1, (t - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setVal(Math.round(to * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

/* ─────────── page ─────────── */

export default function TheCase() {
  const [stats, setStats] = useState({ count: 0, total_pledged: 0 });

  useEffect(() => {
    api.get("/credit-union/stats").then((r) => setStats(r.data || {})).catch(() => {});
  }, []);

  const share = async () => {
    const text =
      "This is why we need our own bank. Sign the pledge + survey — 90 seconds — and move the Proposed Trillion Dollar Spending Power Federal Credit Union one step closer to charter approval.";
    const url = window.location.origin + "/the-case";
    try {
      if (navigator.share) {
        await navigator.share({ title: "The Case For Our Own Bank", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        // eslint-disable-next-line no-alert
        alert("Link copied — share with your people.");
      }
    } catch (_) {}
  };

  const cardsPct = Math.min(100, (stats.count || 0) / 30);
  const capitalPct = Math.min(100, (stats.total_pledged || 0) / 5000);

  return (
    <div className="bg-bdu-bg text-bdu-cream min-h-screen" data-testid="the-case-page">
      {/* ────────── HERO — the hook ────────── */}
      <section
        className="relative overflow-hidden border-b border-bdu-gold/15"
        data-testid="case-hero-section"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(1200px 600px at 80% -10%, rgba(212,175,55,0.14) 0%, rgba(0,0,0,0) 55%), radial-gradient(900px 500px at 10% 100%, rgba(224,224,224,0.08) 0%, rgba(0,0,0,0) 60%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <div
            className="inline-flex items-center gap-2 label-eyebrow"
            style={{ color: "rgba(212,175,55,0.9)" }}
          >
            <AlertTriangle size={14} /> The case for our own bank
          </div>
          <h1
            className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl"
            data-testid="case-headline"
          >
            Every <span className="italic text-bdu-gold">dealer finance desk</span> in America was
            designed to{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #FFFFFF 0%, #E0E0E0 25%, #D4AF37 60%, #B8860B 100%)",
              }}
            >
              extract the Black dollar.
            </span>
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-bdu-cream/75 leading-relaxed max-w-3xl">
            Not by accident. By design. For 50 years the auto-loan playbook has priced the same
            credit profile differently based on who's holding it. Our community pays the gap.
            The{" "}
            <span className="text-bdu-cream font-semibold">
              Proposed Trillion Dollar Spending Power Federal Credit Union
            </span>{" "}
            exists to end it. Your 90-second pledge is what gets it chartered.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#reserve"
              className="inline-flex items-center gap-2 px-8 py-4 uppercase tracking-widest text-xs font-bold text-black transition-all hover:brightness-110 shadow-[0_14px_40px_-10px_rgba(212,175,55,0.6)]"
              style={{
                background:
                  "linear-gradient(135deg, #F7D878 0%, #D4AF37 50%, #B8860B 100%)",
                border: "1px solid #B8860B",
              }}
              data-testid="case-hero-primary-cta"
            >
              Reserve My Spot (20 sec) <ArrowRight size={16} />
            </a>
            <a href="#simulator" className="btn-ghost" data-testid="case-hero-simulator-cta">
              Try The Simulator
            </a>
            <button
              type="button"
              onClick={share}
              className="btn-ghost"
              data-testid="case-hero-share-cta"
            >
              <Share2 size={14} /> Share this page
            </button>
          </div>

          {/* live charter progress — the only numbers that matter */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
            <div className="bg-black/50 backdrop-blur-sm border border-bdu-gold/25 p-5">
              <div className="label-eyebrow">Pledge cards signed</div>
              <div className="mt-2 font-serif text-4xl text-bdu-cream flex items-baseline gap-2">
                <AnimatedCounter to={stats.count || 0} />
                <span className="text-base text-bdu-cream/40">/ 3,000</span>
              </div>
              <div className="mt-3 h-1.5 bg-bdu-cream/10 overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${cardsPct}%`,
                    background:
                      "linear-gradient(90deg, #F7D878 0%, #D4AF37 100%)",
                    transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)",
                  }}
                />
              </div>
            </div>
            <div className="bg-black/50 backdrop-blur-sm border border-bdu-gold/25 p-5">
              <div className="label-eyebrow">Seed capital in escrow</div>
              <div className="mt-2 font-serif text-4xl text-bdu-cream flex items-baseline gap-2">
                $<AnimatedCounter to={stats.total_pledged || 0} />
                <span className="text-base text-bdu-cream/40">/ $500K</span>
              </div>
              <div className="mt-3 h-1.5 bg-bdu-cream/10 overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${capitalPct}%`,
                    background:
                      "linear-gradient(90deg, #F7D878 0%, #D4AF37 100%)",
                    transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ────────── RESERVE MY SPOT — low-friction entry for cold IG traffic ────────── */}
      <ReserveMySpot />

      {/* ────────── PAIN STATS STRIP ────────── */}
      <section className="relative border-b border-bdu-gold/10" data-testid="case-pain-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <AnimateIn>
            <div className="text-center">
              <div className="label-eyebrow text-red-300/80">The data they don't put in the brochure</div>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight max-w-3xl mx-auto">
                Four numbers you need to see before you walk onto another lot.
              </h2>
            </div>
          </AnimateIn>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PAIN_STATS.map((s, i) => (
              <AnimateIn key={s.label} delay={i * 100}>
                <div
                  className="bg-black/60 border border-red-400/20 p-6 h-full hover:border-red-400/50 transition"
                  data-testid={`case-pain-${i}`}
                >
                  <div
                    className="font-serif text-5xl leading-none"
                    style={{
                      background:
                        "linear-gradient(135deg, #F7D878 0%, #D4AF37 60%, #8B6914 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {s.big}
                  </div>
                  <div className="mt-3 label-eyebrow text-red-300/80">{s.label}</div>
                  <p className="mt-3 text-sm text-bdu-cream/70 leading-relaxed">{s.body}</p>
                </div>
              </AnimateIn>
            ))}
          </div>

          <p className="mt-10 text-center text-xs text-bdu-cream/40 italic">
            Sources: CFPB auto-finance disparate-impact studies · DOJ consent orders ·
            National Fair Housing Alliance.
          </p>
        </div>
      </section>

      {/* ────────── INTERACTIVE SIMULATOR ────────── */}
      <LoanSimulator />

      {/* ────────── THE CYCLE ────────── */}
      <section
        className="relative border-b border-bdu-gold/10 overflow-hidden"
        data-testid="case-cycle-section"
        style={{
          background:
            "linear-gradient(180deg, #050505 0%, #0a0a0a 50%, #050505 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <AnimateIn>
            <div className="text-center max-w-3xl mx-auto">
              <div className="label-eyebrow">The cycle of extraction</div>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
                It's not <span className="italic text-bdu-gold">your</span> credit score.
                <br className="hidden sm:block" /> It's a <span className="italic">system</span> built to keep repeating.
              </h2>
            </div>
          </AnimateIn>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {/* connector line (desktop) */}
            <div
              className="hidden lg:block absolute left-0 right-0 top-16 h-px pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, rgba(212,175,55,0) 0%, rgba(212,175,55,0.5) 15%, rgba(212,175,55,0.5) 85%, rgba(212,175,55,0) 100%)",
              }}
            />
            {CYCLE_NODES.map((n, i) => (
              <AnimateIn key={n.title} delay={i * 150}>
                <div className="relative" data-testid={`case-cycle-${i}`}>
                  <div className="flex items-center gap-3">
                    <div
                      className="relative w-10 h-10 rounded-full flex items-center justify-center border border-bdu-gold/50"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(212,175,55,0.15), rgba(0,0,0,0.6))",
                      }}
                    >
                      <n.icon size={18} className="text-bdu-gold" />
                    </div>
                    <div
                      className="font-serif text-2xl text-bdu-gold"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="mt-5 font-serif text-xl text-bdu-cream leading-snug">
                    {n.title}
                  </div>
                  <p className="mt-2 text-sm text-bdu-cream/60 leading-relaxed">{n.body}</p>
                </div>
              </AnimateIn>
            ))}
          </div>

          <AnimateIn delay={400}>
            <div className="mt-16 bg-black/70 border border-red-400/30 px-6 py-5 flex flex-wrap items-center justify-between gap-4 max-w-3xl mx-auto">
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} className="text-red-300 flex-shrink-0" />
                <div>
                  <div className="font-sans font-semibold text-bdu-cream">
                    This cycle runs 7–10 million times a year.
                  </div>
                  <div className="text-xs text-bdu-cream/55 mt-0.5">
                    Every single one of them is a transfer of wealth OUT of our community.
                  </div>
                </div>
              </div>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* ────────── WHAT CHANGES ────────── */}
      <section
        className="relative border-b border-bdu-gold/10"
        data-testid="case-changes-section"
      >
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <AnimateIn>
            <div className="text-center max-w-3xl mx-auto">
              <div className="label-eyebrow text-bdu-gold">What changes when we own the bank</div>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
                A member-owned credit union <span className="italic text-bdu-gold">rewrites every line</span> of that story.
              </h2>
              <p className="mt-5 text-base text-bdu-cream/65 max-w-2xl mx-auto">
                Not with charity. With structure. A federal credit union is legally required to serve
                members, not shareholders. That changes who the system is designed for.
              </p>
            </div>
          </AnimateIn>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {CHANGES.map((c, i) => (
              <AnimateIn key={c.title} delay={i * 120}>
                <div
                  className="bg-bdu-surface2 border border-bdu-gold/20 p-6 h-full hover:border-bdu-gold/60 hover:-translate-y-0.5 transition"
                  data-testid={`case-change-${i}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center border border-bdu-gold/50"
                      style={{
                        background:
                          "linear-gradient(135deg, rgba(247,216,120,0.2), rgba(139,105,20,0.1))",
                      }}
                    >
                      <c.icon size={22} className="text-bdu-gold" />
                    </div>
                    <div className="font-serif text-xl text-bdu-cream leading-snug">
                      {c.title}
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-bdu-cream/70 leading-relaxed">{c.body}</p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* ────────── THE MATH ────────── */}
      <section
        className="relative border-b border-bdu-gold/10 overflow-hidden"
        data-testid="case-math-section"
        style={{
          background:
            "linear-gradient(180deg, #0a0a0a 0%, #050505 50%, #0a0a0a 100%)",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-24 text-center">
          <AnimateIn>
            <div className="label-eyebrow">The math on charter approval</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-[1.05]">
              Two numbers. One result.
            </h2>
          </AnimateIn>

          <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
            <AnimateIn delay={100}>
              <div className="text-center">
                <div
                  className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-none"
                  style={{
                    background:
                      "linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #8B6914 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  3,000
                </div>
                <div className="mt-2 label-eyebrow">Pledge membership cards</div>
              </div>
            </AnimateIn>
            <div className="text-5xl text-bdu-gold/60 font-serif">+</div>
            <AnimateIn delay={250}>
              <div className="text-center">
                <div
                  className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-none"
                  style={{
                    background:
                      "linear-gradient(135deg, #FFFFFF 0%, #D4AF37 50%, #8B6914 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  $500K
                </div>
                <div className="mt-2 label-eyebrow">Seed capital in escrow</div>
              </div>
            </AnimateIn>
            <div className="text-5xl text-bdu-gold/60 font-serif">=</div>
            <AnimateIn delay={400}>
              <div className="text-center">
                <div
                  className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-none italic"
                  style={{
                    background:
                      "linear-gradient(135deg, #F7D878 0%, #FFFFFF 50%, #D4AF37 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  Charter.
                </div>
                <div className="mt-2 label-eyebrow text-bdu-gold">Granted by the NCUA</div>
              </div>
            </AnimateIn>
          </div>

          <AnimateIn delay={600}>
            <p className="mt-16 text-base sm:text-lg text-bdu-cream/70 max-w-2xl mx-auto leading-relaxed">
              Everything else is already built — the organizers, the business plan, the escrow at
              Citizens Trust Bank, the field-of-membership study, the policies, the technology
              stack. We are waiting on <span className="text-bdu-cream font-semibold">you</span>.
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* ────────── THE ASK ────────── */}
      <section
        className="relative border-b border-bdu-gold/10"
        data-testid="case-ask-section"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-24">
          <AnimateIn>
            <div className="text-center max-w-3xl mx-auto">
              <div className="label-eyebrow text-bdu-gold">The whole ask</div>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
                Three things. Nine minutes. For the next generation of our community.
              </h2>
            </div>
          </AnimateIn>

          <div className="mt-14 space-y-5">
            {ASKS.map((a, i) => (
              <AnimateIn key={a.label} delay={i * 150}>
                <div
                  className="group bg-bdu-surface2 border border-bdu-gold/20 p-6 flex items-start gap-5 hover:border-bdu-gold/60 hover:bg-black/40 transition"
                  data-testid={`case-ask-${i}`}
                >
                  <div
                    className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center border border-bdu-gold/50"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(247,216,120,0.25), rgba(139,105,20,0.1))",
                    }}
                  >
                    <CheckCircle2 size={22} className="text-bdu-gold" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-3">
                      <div
                        className="font-serif text-lg sm:text-xl text-bdu-cream leading-snug"
                      >
                        {a.label}
                      </div>
                      <div className="font-serif text-sm text-bdu-gold/60 flex-shrink-0">
                        #{i + 1}
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-bdu-cream/65 leading-relaxed">{a.detail}</p>
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>

          <AnimateIn delay={400}>
            <div className="mt-14 text-center">
              <Link
                to="/credit-union#subscribe"
                className="inline-flex items-center gap-2 px-10 py-5 uppercase tracking-widest text-sm font-bold text-black transition-all hover:brightness-110 shadow-[0_18px_60px_-15px_rgba(212,175,55,0.7)]"
                style={{
                  background:
                    "linear-gradient(135deg, #F7D878 0%, #D4AF37 50%, #B8860B 100%)",
                  border: "1px solid #B8860B",
                }}
                data-testid="case-final-cta"
              >
                Sign Pledge + Survey Now <ArrowRight size={18} />
              </Link>
              <p className="mt-5 text-xs uppercase tracking-widest text-bdu-cream/40">
                90 seconds · $100 refundable · pending NCUA charter approval
              </p>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* ────────── FINAL COMPLIANCE FOOTER ────────── */}
      <section
        className="bg-black/80 border-t border-bdu-gold/15 py-12"
        data-testid="case-compliance-footer"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <div className="inline-flex items-center gap-2 label-eyebrow text-bdu-cream/60">
            <Lock size={12} /> NCUA Compliance Notice
          </div>
          <p className="mt-5 text-xs sm:text-sm text-bdu-cream/55 leading-relaxed">
            This page is educational and solicits <span className="text-bdu-cream/80">charter support only</span>.
            The Proposed Trillion Dollar Spending Power Federal Credit Union is pending NCUA
            charter approval and is not yet operational. It cannot accept deposits, open accounts,
            or originate loans. No loan rate, product, or approval is promised or implied by this page.
            Pledges are non-binding expressions of interest, held in a segregated escrow account at
            Citizens Trust Bank, and are refunded in full if the charter is not granted. Survey responses
            are collected pursuant to NCUA Chapter 2 field-of-membership study requirements and are
            transmitted over TLS, stored encrypted at rest, and never sold, leased, or shared.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-bdu-cream/60">
            <Link to="/credit-union" className="hover:text-bdu-gold">
              Full charter details
            </Link>
            <span className="text-bdu-cream/20">·</span>
            <Link to="/field-of-membership" className="hover:text-bdu-gold">
              Field of membership
            </Link>
            <span className="text-bdu-cream/20">·</span>
            <a href="mailto:greg@blackdollarsunited.org" className="hover:text-bdu-gold">
              greg@blackdollarsunited.org
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
